<template>
  <div class="m-color-picker">
    <div
      class="m-color-picker__box"
      :style="`
        background-color: rgb(${color.red}, ${color.green},${color.blue});
        width: ${width}px;
        height: ${height}px`"
      @click="toggleSelector()">
    </div>
    <transition name="fade">
      <div
        v-if="showSelector"
        :class="`m-color-picker__selector ${pickerSelectorPositionClass}`">
        <a
          class="m-color-picker__close"
          href="#" tabindex="0"
          @click.prevent="toggleSelector">
            <icon name="times" />
        </a>
        <div class="m-color-picker__sliders">
          <input
            type="range"
            min="0"
            max="255"
            step="1"
            v-model="color.red"
            data-test="color-slider-r"
            @change="updateColor"/>
          <b>R</b> {{ color.red }}<br />
          <input
            type="range"
            min="0"
            max="255"
            step="1"
            v-model="color.green"
            data-test="color-slider-g"
            @change="updateColor"/>
          <b>G</b> {{ color.green }}<br />
          <input
            type="range"
            min="0"
            max="255"
            step="1"
            v-model="color.blue"
            data-test="color-slider-b"
            @change="updateColor"/>
          <b>B</b> {{ color.blue }}
        </div>
        <div class="m-color-picker__palette">
          <div
            v-for="(c, i, key) in palette"
            class="m-color-picker__palette-color" :key="key"
            :style="`background-color: rgb(${c.r}, ${c.g}, ${c.b})`"
            @click="paletteSelect(c)"/>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  name: 'v-color-picker',
  components: {},
  props: {
    value: {
      type: [String],
      required: true,
    },
    width: {
      type: [String],
      default: '45',
    },
    height: {
      type: [String],
      default: '45',
    },
    position: {
      type: [String],
      default: 'bottom',
    },
  },
  data() {
    return {
      color: {
        red: 40,
        green: 40,
        blue: 40,
      },
      palette: [ // Should contain 16 rgb values
        { r: 0, g: 152, b: 206 },
        { r: 73, g: 80, b: 156 },
        { r: 204, g: 219, b: 41 },
        { r: 0, g: 0, b: 0 },
        { r: 100, g: 100, b: 100 },
        { r: 255, g: 255, b: 255 },
        { r: 15, g: 50, b: 150 },
        { r: 255, g: 186, b: 55 },
        { r: 44, g: 150, b: 38 },
        { r: 204, g: 48, b: 48 },
        { r: 202, g: 36, b: 210 },
        { r: 22, g: 135, b: 171 },
        { r: 255, g: 123, b: 0 },
        { r: 80, g: 53, b: 45 },
        { r: 45, g: 200, b: 180 },
        { r: 255, g: 184, b: 239 },
        { r: 50, g: 50, b: 50 },
        { r: 155, g: 204, b: 109 },
      ],
      showSelector: false,
    };
  },
  created() {
    this.handleInput(this.value);
  },
  computed: {
    hexColor() {
      const hexRed = this.decToHex(this.color.red);
      const hexGreen = this.decToHex(this.color.green);
      const hexBlue = this.decToHex(this.color.blue);

      return `#${hexRed}${hexGreen}${hexBlue}`;
    },
    pickerSelectorPositionClass() {
      return `m-color-picker__selector--${this.position}`;
    },
  },
  methods: {
    updateColor() {
      this.$emit('input', this.hexColor);
      console.log(this.hexColor);
    },
    paletteSelect(selected) {
      this.color.red = selected.r;
      this.color.green = selected.g;
      this.color.blue = selected.b;

      this.updateColor();
    },
    decToHex(value) {
      let hex = parseInt(value, 10).toString(16);

      if (hex.length < 2) hex = `0${hex}`;

      return hex;
    },
    hexToDec(value) { // Split a whole 6 digit hex string into RGB values
      this.color.red = parseInt(value.slice(1, 3), 16);
      this.color.green = parseInt(value.slice(3, 5), 16);
      this.color.blue = parseInt(value.slice(5, 7), 16);
    },
    toggleSelector() {
      if (this.showSelector === true) {
        this.showSelector = false;
      } else {
        this.showSelector = true;
      }
    },
    handleInput(hex) {
      const conditions = /^#([0-9a-fA-F]{2}){3}$/g;

      if (conditions.test(hex) === true) {
        this.hexToDec(hex);
        this.updateColor();
      }
    },
  },
  watch: {
    value: {
      handler(hex) {
        this.handleInput(hex);
      },
      deep: true,
    },
  },
};
</script>

<style lang="scss" scoped>
  @import '../styles/component-utils';

  $parent: '.m-color-picker';

  #{$parent} {
    display: inline-block;
    position: relative;

    &__box {
      display: inline-block;
      background: grey;
      border: 1px solid #DBE0E5;
      border-radius: 3px;
      transition: background-color 0.3s ease;
      cursor: pointer;
    }

    &__palette {
      padding-top: $spacing-small;
    }

    &__palette-color {
      width: 23px;
      height: 23px;
      background: red;
      margin-right: $spacing-small;
      display: inline-block;
      margin-bottom: $spacing-xs;
      margin-top: 0;
      border: 1px solid $color-border;
      cursor: pointer;

      &:nth-of-type(6n) {
        margin-right: 0;
      }
    }

    &__selector {
      z-index: 50;
      width: 210px;
      border: 1px solid $signable-primary-1;
      display: inline-block;
      padding: $spacing-small;
      padding-bottom: 0;
      background: #fff;
      position: absolute;
      transform: translate(calc(-50% - 30px), 0);
      border-radius: 3px;
      box-shadow: 0px 0px 10px -3px $signable-primary-1;
      transition: border-color .2s ease, opacity .2s ease, box-shadow .2s ease;
      &--bottom {
        top: 50px;
      }
      &--top {
        bottom: 50px;
      }
    }

    &__close {
      z-index: 3;
      line-height: 0;
      background: #fff;
      border-radius: 30px;
      position: absolute;
      right: -$spacing-small;
      top: -$spacing-small;
      padding: $spacing-xs;
      border: 3px solid $color-error;
      &:hover {
        background: $color-error;
        svg {
          color: #fff;
        }
      }
      svg {
        height: 12px;
        width: 12px;
        color: $color-error;
      }
    }

    &__sliders {
      padding-bottom: $spacing-base;
      border-bottom: 1px solid $color-border;
      input {
        width: 129px;
        vertical-align: middle;
        margin-right: $spacing-small;
      }
    }

    b {
      display: inline !important;
    }
  }

  .fade-enter-active, .fade-leave-active {
    transition: opacity .5s;
  }
  .fade-enter, .fade-leave-to {
    opacity: 0;
  }
</style>
